import { type PropsWithChildren } from 'react';
import Emoji from 'react-emoji-render';
import { motion, useTransform } from 'framer-motion';
import { graphql, useStaticQuery } from 'gatsby';
import styled from 'styled-components';

import { LinkWithQuery } from '@/components/global/LinkWithQuery';
import { ContentMarginLine } from '@/components/v2/GridLines';
import { LayoutSubgrid } from '@/components/v2/Page/LayoutGrid';
import { ReactComponent as ArrowRight } from '@/images/icons/icon-arrow-right.svg';
import { TRACKING_LABELS } from '@/utils';

import { useHeaderUI, useHeaderUIRefs } from './HeaderUIProvider';

const Bg = styled(LayoutSubgrid)`
  overflow: hidden;
  isolation: isolate;
  background-color: ${({ theme }) => theme.palette.fg400};
  background-image: ${({ theme }) =>
    `linear-gradient(to left, ${theme.palette.fg400}, ${theme.palette.fg300}, ${theme.palette.fg400})`};

  box-shadow: ${({ theme }) => {
    return [`0 1px 1px 0px ${theme.palette.fgA700} inset`].join(',');
  }};
`;

function AnimatedBg({ children }: PropsWithChildren) {
  const { smoothSquishProgress } = useHeaderUI();
  const { tickerRef } = useHeaderUIRefs();

  const opacity = useTransform(smoothSquishProgress, [0, 1], [1, 0]);
  const y = useTransform(smoothSquishProgress, [0, 1], ['0rem', '0.5rem']);

  return (
    <Bg
      ref={tickerRef}
      as={motion.div}
      data-content-grid
      style={{
        opacity,
        y
      }}>
      {children}
    </Bg>
  );
}

const InstanceAnim = styled.div<{ $duration: number; $instances?: number }>`
  animation: ${({ $duration }) => $duration}s ticker linear infinite;
  display: flex;
  width: fit-content;

  &:has(a:hover, a:focus-visible) {
    animation-play-state: paused;
  }

  > * {
    white-space: nowrap;
    display: flex;
  }

  @keyframes ticker {
    from {
      transform: translateX(0%);
    }
    to {
      transform: translateX(
        calc(-100% / ${({ $instances }) => $instances || 3})
      );
    }
  }
`;

function Instancer({
  children,
  instances = 3,
  duration = 120
}: PropsWithChildren<{ duration?: number; instances?: number }>) {
  return (
    <InstanceAnim
      $duration={duration}
      $instances={instances}
      aria-hidden="true"
      tabIndex={-1}>
      {Array.from({ length: instances }, (_, i) => {
        return <span key={i}>{children}</span>;
      })}
    </InstanceAnim>
  );
}

const Marquee = styled.div`
  grid-column: 1 / -1;
  grid-row: 1;
  z-index: 1;
  position: relative;
  display: flex;
  overflow: hidden;
`;

const MarqueeItems = styled.span`
  display: flex;
  gap: 1rem;
  padding: 0.25rem 0;
  padding-right: 1rem;
`;

const MarqueeItem = styled(LinkWithQuery)`
  white-space: nowrap;
  display: flex;
  padding: 0 0.5rem;
  border-radius: 2px;
  border-bottom: none;
  align-items: center;

  font-size: ${({ theme }) => theme.fontSize.body.s};
  font-family: ${({ theme }) => theme.fontFamily.MONO_ALT};
  font-weight: ${({ theme }) => theme.fontWeight.mono};

  &:hover {
    background-color: ${({ theme }) => theme.palette.fg200};
  }
`;

const MarqueeItemTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.body.s};
  font-family: ${({ theme }) => theme.fontFamily.MONO_ALT};
  font-weight: ${({ theme }) => theme.fontWeight.mono};
  color: ${({ theme }) => theme.palette.fgLoud};
`;

const StyledArrowRight = styled(ArrowRight)`
  &,
  & > * {
    fill: currentColor;
  }
`;

const Circle = styled.span<{ $isExternal: boolean }>`
  display: grid;
  place-items: center;
  margin-left: 0.25rem;
  width: 1.25em;
  aspect-ratio: 1 / 1;
  border-radius: 9999px;
  color: ${({ theme }) => theme.palette.fgA1100};

  transform-origin: center center;
  transform: ${({ $isExternal }) => ($isExternal ? 'rotate(-45deg)' : 'none')};
`;

export function Ticker() {
  const data = useStaticQuery<{
    allContentfulTicker: {
      nodes: Array<{
        emoji: string;
        link: string;
        secondaryText: string;
        title: string;
      }>;
    };
  }>(graphql`
    query {
      allContentfulTicker(sort: { createdAt: DESC }) {
        nodes {
          title
          secondaryText
          emoji
          link
        }
      }
    }
  `);
  const tickerEntries = data.allContentfulTicker.nodes;

  return (
    <>
      <ContentMarginLine data-minor />
      <AnimatedBg>
        <Marquee aria-label="announcements" role="marquee">
          <div className="sr-only">
            {tickerEntries.map(entry => {
              const isExternal = entry.link.includes('https');
              return (
                <MarqueeItem
                  key={entry.title}
                  isExternal={isExternal}
                  to={entry.link}
                  trackingLabel={TRACKING_LABELS.TickerItem}>
                  <Emoji className="" text={entry.emoji} />
                  &nbsp;
                  <MarqueeItemTitle>{entry.title}</MarqueeItemTitle>&nbsp;
                  {entry.secondaryText}
                  <Circle $isExternal={isExternal}>
                    <StyledArrowRight />
                  </Circle>
                </MarqueeItem>
              );
            })}
          </div>
          <Instancer duration={tickerEntries.length * 14}>
            <MarqueeItems>
              {tickerEntries.map(entry => {
                const isExternal = entry.link.includes('https');
                return (
                  <MarqueeItem
                    key={entry.title}
                    aria-hidden="true"
                    isExternal={isExternal}
                    tabIndex={-1}
                    to={entry.link}
                    trackingLabel={TRACKING_LABELS.TickerItem}>
                    <Emoji text={entry.emoji} />
                    &nbsp;
                    <MarqueeItemTitle>{entry.title}</MarqueeItemTitle>&nbsp;
                    {entry.secondaryText}
                    <Circle $isExternal={isExternal}>
                      <StyledArrowRight />
                    </Circle>
                  </MarqueeItem>
                );
              })}
            </MarqueeItems>
          </Instancer>
        </Marquee>
      </AnimatedBg>
      <ContentMarginLine data-minor data-right />
    </>
  );
}
